import { useState, useEffect, useCallback } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useLocation } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getOffices } from '@/api/getOffices';
import { getSchools } from '@/api/getSchools';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { CreateOrEditSchoolDialog } from '@/components/schools/CreateOrEditSchoolDialog';
import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { School } from '@/types/school';

export const SchoolsPage = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>(undefined);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isEditOpenDialog, setIsEditOpenDialog] = useState(false);

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '学校マスタ' }];

  const [loginUser] = useAtom(UserAtom);
  const [_, setNotification] = useAtom(NotificationAtom);

  const fetchSchools = useCallback(async () => {
    try {
      const result = await getSchools(loginUser?.email);
      if (result.status === 200) {
        setSchools(result.result);
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '学校マスタの取得に失敗しました',
          message: '学校マスタの取得に失敗しました',
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '学校マスタの取得に失敗しました',
        message: '学校マスタの取得に失敗しました',
      });
    }
  }, [loginUser?.email]);

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(loginUser?.email || '');
      if (result.status === 200) {
        setOffices(result.result);
      }
    } catch (error) {
      console.log('error');
    }
  }, [loginUser?.email]);

  useEffect(() => {
    fetchSchools();
    fetchOffices();
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 mt-5 flex flex-col items-start justify-between gap-5">
        <h1 className="text-2xl font-bold">学校マスタ</h1>
        <div className="flex-shrink-0">
          {loginUser?.is_super_admin && (
            <Button
              type="submit"
              size="lg"
              variant="solid-fill"
              onClick={() => setIsOpenCreateDialog(true)}
            >
              新規登録
            </Button>
          )}
        </div>
      </div>
      {isOpenCreateDialog && (
        <CreateOrEditSchoolDialog
          isOpen={isOpenCreateDialog}
          setIsOpen={setIsOpenCreateDialog}
          afterSubmit={() => {
            fetchSchools();
          }}
          offices={offices}
        />
      )}

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            No
          </Column>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            学校名
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            学校種類
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            郵便番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            都道府県
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            市区町村
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">住所</Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            電話番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">編集</Column>
        </TableHeader>
        <TableBody>
          {schools.map((school, index) => (
            <Row key={index}>
              <Column
                key="no"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.id}
              </Column>
              <Column
                key="name"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.name}
              </Column>
              <Column
                key="type"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.type}
              </Column>
              <Column
                key="postalCode"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.postalCode}
              </Column>
              <Column
                key="prefectureId"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {PREFECTURE_OPTIONS.find((pref) => Number(pref.id) === school.prefectureId)?.name ||
                  '不明'}
              </Column>
              <Column
                key="municipality"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.municipality}
              </Column>
              <Column
                key="address"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.address1} {school.address2}
              </Column>
              <Column
                key="phoneNumber"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {school.phoneNumber}
              </Column>

              <Column
                key="edit"
                className="border border-gray-200 px-4 py-2"
              >
                <Button
                  size="sm"
                  variant="text"
                  type="submit"
                  onClick={() => {
                    setIsEditOpenDialog(true);
                    setSelectedSchool(school);
                  }}
                >
                  編集
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>
      {isEditOpenDialog && (
        <CreateOrEditSchoolDialog
          isOpen={isEditOpenDialog}
          setIsOpen={setIsEditOpenDialog}
          afterSubmit={() => {
            fetchSchools();
          }}
          school={selectedSchool}
          offices={offices}
        />
      )}
    </div>
  );
};
